import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService, PagedRequest, PagedResponse } from '../base/base-api.service';

// These types should match the models from @marina/models
export interface Vessel {
  id: string;
  customerId: string;
  customerName?: string | null;
  /** Account type of the vessel owner: PERSONAL, BUSINESS, or BOAT_CLUB. */
  customerType?: string | null;
  vesselName: string;
  make: string;
  model: string;
  year: number;
  vesselType: string;
  length: number;
  beam: number;
  draft: number;
  height: number;
  weight: number;
  registrationNumber: string;
  hin: string;
  engineType?: string | null;
  engineHp?: number | null;
  fuelTankCapacityGallons?: number | null;
  insuranceId?: string;
  insuranceStatus?: string; // EXPIRED, VERIFIED, PENDING, NONE, ON_FILE (Boat Club fleet)
  insurance?: import('@marina/models').InsuranceDto | null; // Latest insurance object
  lease?: import('@marina/models').LeaseDto | null; // Most relevant lease/contract (prefers Contracted, then most recent)
  photoUrl?: string;
  notes?: string | null;
  /** Vessel status derived from contracts. System-managed (New, Contracted, Terminated). */
  vesselStatus?: string;
  createdAt: string;
  updatedAt: string;
  /** True when vessel is archived (hidden from active fleet). */
  isArchived?: boolean;
  archivedAt?: string | null;
  // Last return snapshot populated server-side from the most recent RETURNED launch
  lastReturnLocationName?: string | null;
  lastReturnIsTemporary?: boolean | null;
  lastReturnedAt?: string | null;
  lastReturnConditionIssues?: boolean | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  slipId?: string | null;
  dryDockLocationId?: string | null;
  permanentLocationName?: string | null;
  temporaryLocationId?: string | null;
  temporaryLocationName?: string | null;
}

export interface CreateVesselRequest {
  customerId: string;
  vesselName: string;
  make: string;
  model: string;
  year: number;
  vesselType: string;
  length: number;
  beam: number;
  draft: number;
  height: number;
  weight: number;
  registrationNumber: string;
  hin: string;
  engineType?: string;
  engineHp?: number;
  fuelTankCapacityGallons?: number;
  photoUrl?: string;
  notes?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  slipId?: string | null;
  dryDockLocationId?: string | null;
}

export interface UpdateVesselRequest {
  customerId?: string;
  vesselName?: string;
  make?: string;
  model?: string;
  year?: number;
  vesselType?: string;
  length?: number;
  beam?: number;
  draft?: number;
  height?: number;
  weight?: number;
  registrationNumber?: string;
  hin?: string;
  engineType?: string;
  engineHp?: number;
  fuelTankCapacityGallons?: number;
  insuranceId?: string;
  photoUrl?: string;
  notes?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
}

export interface UpdateVesselSlipRequest {
  slipId?: string | null;
  dryDockLocationId?: string | null;
  /** When true, clears the vessel's temporary dry-storage slot (Boat Club). */
  clearTemporaryLocation?: boolean;
}

export interface VesselFilterRequest extends PagedRequest {
  insuranceStatus?: string; // ALL, EXPIRED, VERIFIED, PENDING, NONE, ON_FILE
  customerId?: string;
  vesselStatus?: string;
  hasActiveContractOnly?: boolean; // When true, only return vessels with an active contract
  /** active (default): non-archived only; archived: archived only; all: both */
  archiveScope?: 'active' | 'archived' | 'all';
  /** Primary lease uses this wet slip (server-side). */
  slipId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class VesselService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http);
  }
  /**
   * Get vessel by ID
   */
  getById(id: string): Observable<Vessel> {
    return this.get<Vessel>(`vessels/${id}`);
  }

  /**
   * Get all vessels with pagination
   */
  getAll(request?: PagedRequest): Observable<PagedResponse<Vessel>> {
    return this.getPaged<Vessel>('vessels', request);
  }

  /**
   * Get all vessels with filtering and sorting
   */
  getAllFiltered(request?: VesselFilterRequest): Observable<PagedResponse<Vessel>> {
    // Build params including insuranceStatus and customerId
    let params = this.getPagedParams(request);
    
    if (request?.insuranceStatus) {
      params = params.set('insuranceStatus', request.insuranceStatus);
    }
    
    if (request?.customerId) {
      params = params.set('customerId', request.customerId);
    }
    if (request?.hasActiveContractOnly === true) {
      params = params.set('hasActiveContractOnly', 'true');
    }

    if (request?.vesselStatus) {
      params = params.set('vesselStatus', request.vesselStatus);
    }
    if (request?.archiveScope) {
      params = params.set('archiveScope', request.archiveScope);
    }
    if (request?.slipId) {
      params = params.set('slipId', request.slipId);
    }
    
    return this.get<PagedResponse<Vessel>>('vessels/filtered', params);
  }

  /**
   * Filtered vessels for the current customer portal user (scoped to login + captain access).
   */
  getMyVesselsFiltered(request?: VesselFilterRequest): Observable<PagedResponse<Vessel>> {
    let params = this.getPagedParams(request);

    if (request?.insuranceStatus) {
      params = params.set('insuranceStatus', request.insuranceStatus);
    }
    if (request?.vesselStatus) {
      params = params.set('vesselStatus', request.vesselStatus);
    }
    if (request?.hasActiveContractOnly === true) {
      params = params.set('hasActiveContractOnly', 'true');
    }
    if (request?.archiveScope) {
      params = params.set('archiveScope', request.archiveScope);
    }
    if (request?.slipId) {
      params = params.set('slipId', request.slipId);
    }

    return this.get<PagedResponse<Vessel>>('vessels/me/filtered', params);
  }

  /**
   * Get vessels by customer ID
   */
  getByCustomerId(customerId: string, request?: PagedRequest): Observable<PagedResponse<Vessel>> {
    return this.getPaged<Vessel>(`vessels/customer/${customerId}`, request);
  }

  /**
   * Create new vessel
   */
  create(request: CreateVesselRequest): Observable<Vessel> {
    return this.post<Vessel>('vessels', request);
  }

  /**
   * Create new vessel for current customer (customer-specific endpoint)
   */
  createMyVessel(request: CreateVesselRequest): Observable<Vessel> {
    return this.post<Vessel>('vessels/me', request);
  }

  /**
   * Update vessel
   */
  update(id: string, request: UpdateVesselRequest): Observable<Vessel> {
    return this.put<Vessel>(`vessels/${id}`, request);
  }

  /**
   * Boat Club: update permanent dry-storage slot or clear temporary location.
   */
  updateSlip(id: string, request: UpdateVesselSlipRequest): Observable<Vessel> {
    return this.patch<Vessel>(`vessels/${id}/slip`, request);
  }

  /**
   * Update vessel for current customer (customer-specific endpoint)
   */
  updateMyVessel(id: string, request: UpdateVesselRequest): Observable<Vessel> {
    return this.put<Vessel>(`vessels/me/${id}`, request);
  }

  /**
   * Delete vessel
   */
  deleteVessel(id: string): Observable<void> {
    return this.deleteRequest(`vessels/${id}`);
  }

  /**
   * Upload photo for vessel
   */
  uploadPhoto(id: string, file: File): Observable<{ photoUrl: string; updatedAt?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    // For file uploads, we need to let the browser set Content-Type automatically
    // to include the multipart/form-data boundary
    // Create headers without Content-Type so browser can set it with boundary
    const headers = new HttpHeaders();
    
    return this.post<{ photoUrl: string; updatedAt?: string }>(`vessels/${id}/upload-photo`, formData, headers);
  }

  /**
   * Upload photo for current customer's vessel (customer-specific endpoint)
   */
  uploadMyVesselPhoto(id: string, file: File): Observable<{ photoUrl: string; updatedAt?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    // For file uploads, we need to let the browser set Content-Type automatically
    // to include the multipart/form-data boundary
    // Create headers without Content-Type so browser can set it with boundary
    const headers = new HttpHeaders();
    
    return this.post<{ photoUrl: string; updatedAt?: string }>(`vessels/me/${id}/upload-photo`, formData, headers);
  }

  /**
   * Delete photo for vessel
   */
  deletePhoto(id: string): Observable<{ photoUrl: string | null; updatedAt?: string }> {
    return this.deleteRequest<{ photoUrl: string | null; updatedAt?: string }>(`vessels/${id}/photo`);
  }

  /**
   * Delete photo for current customer's vessel (customer-specific endpoint)
   */
  deleteMyVesselPhoto(id: string): Observable<{ photoUrl: string | null; updatedAt?: string }> {
    return this.deleteRequest<{ photoUrl: string | null; updatedAt?: string }>(`vessels/me/${id}/photo`);
  }

  /** Staff: archive vessel (hidden from active fleet). */
  archive(id: string): Observable<Vessel> {
    return this.post<Vessel>(`vessels/${id}/archive`, {});
  }

  /** Staff: restore archived vessel to active fleet. */
  restore(id: string): Observable<Vessel> {
    return this.post<Vessel>(`vessels/${id}/restore`, {});
  }
}

